import { useRef, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import Swal from 'sweetalert2'
import withReactContent from 'sweetalert2-react-content'
import DashboardWrapper from '../../Dashboard/components/DashboardWrapper'
import Button from '../../../components/Button'
import Loader from '../../../components/Loader'
import Toast from '../../../components/Toast/Toast'
import { handleOpenModal } from '../../../utils'
import { HUTRIT_PRIMARY_COLOR } from '../../../utils/constants'
import { PRIVATE_ROUTES } from '../../../Routes/routes'
import {
  deleteCourseVideo,
  getCourseOne,
  getVideosByCourse,
} from '../../../services/Courses'
import { TAGS } from '../../../services/Tags'
import VideoFormModal, { VIDEO_FORM_MODAL_ID } from './components/VideoFormModal'

const BRAND = '#01A76A'
const BRAND_LIGHT = '#00CF83'

// La preview puede venir como imagen estática (thumbnail) o como un clip corto
// de video generado por el backend a partir de previewStart/previewSeconds.
const isImagePreview = (url) => /\.(png|jpe?g|webp|gif|avif)(\?.*)?$/i.test(url || '')

// ─── Card de video del curso (look & feel alineado a MasterClassCard) ────────
const CourseVideoCard = ({ video, displayNumber, onEdit, onDelete }) => {
  const previewUrl = video.previewUrl || video.preview_url || null
  const pdfUrl = video.pdf || null
  const previewIsImage = isImagePreview(previewUrl)
  const videoRef = useRef(null)

  const handleMouseEnter = () => {
    const el = videoRef.current
    if (!el) return
    el.currentTime = 0
    el.play().catch(() => {})
  }

  const handleMouseLeave = () => {
    const el = videoRef.current
    if (!el) return
    el.pause()
    el.currentTime = 0
  }

  return (
    <div
      className="video-course-card d-flex flex-column flex-md-row w-100"
      style={{
        background: '#fff',
        borderRadius: 18,
        border: '1.5px solid #e8ecf0',
        boxShadow: '0 4px 18px rgba(0,0,0,0.06)',
        overflow: 'hidden',
        transition: 'transform 0.25s ease, box-shadow 0.25s ease',
      }}
    >
      <div
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        className="flex-shrink-0"
        style={{
          position: 'relative',
          width: '100%',
          maxWidth: 320,
          aspectRatio: '16 / 9',
          background: '#0f172a',
          overflow: 'hidden',
        }}
      >
        {previewUrl && previewIsImage ? (
          <img
            src={previewUrl}
            alt={video.title}
            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
          />
        ) : (
          <video
            ref={videoRef}
            src={previewUrl || video.url}
            poster={video.url ? `${video.url}#t=${video.previewStart || 1}` : undefined}
            muted
            loop
            playsInline
            preload="metadata"
            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
          />
        )}

        <div
          style={{
            position: 'absolute',
            inset: 0,
            background:
              'linear-gradient(to top, rgba(0,0,0,0.55) 0%, rgba(0,0,0,0) 45%)',
            pointerEvents: 'none',
          }}
        />

        <span
          className="d-flex align-items-center justify-content-center fw-bolder text-white"
          style={{
            position: 'absolute',
            top: 10,
            left: 10,
            width: 32,
            height: 32,
            borderRadius: 10,
            fontSize: 13,
            background: `linear-gradient(135deg, ${BRAND} 0%, ${BRAND_LIGHT} 100%)`,
            boxShadow: '0 2px 8px rgba(1,167,106,0.35)',
          }}
        >
          {displayNumber}
        </span>

        {video.previewSeconds ? (
          <span
            style={{
              position: 'absolute',
              top: 10,
              right: 10,
              background: 'rgba(15,23,42,0.55)',
              border: '1px solid rgba(255,255,255,0.35)',
              color: '#fff',
              borderRadius: 20,
              padding: '3px 10px',
              fontSize: 11,
              fontWeight: 700,
              backdropFilter: 'blur(2px)',
            }}
          >
            Preview {video.previewSeconds}s
          </span>
        ) : null}

        <p
          className="mb-0 fw-bold text-white"
          style={{
            position: 'absolute',
            left: 12,
            right: 12,
            bottom: 8,
            fontSize: 14,
            lineHeight: 1.3,
            textShadow: '0 1px 4px rgba(0,0,0,0.5)',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
          }}
        >
          {video.title}
        </p>
      </div>

      <div className="p-3 d-flex flex-column flex-grow-1" style={{ minWidth: 0 }}>
        <p
          className="fw-bold text-on-bg-primary mb-1 d-md-none"
          style={{ fontSize: 15 }}
        >
          {video.title}
        </p>
        {video.description && (
          <p
            className="text-on-bg-secondary mb-3 flex-grow-1"
            style={{ fontSize: 13.5, lineHeight: 1.5 }}
          >
            {video.description}
          </p>
        )}

        <div className="d-flex gap-2 mt-auto flex-wrap">
          <Button variant="secondary" isSmall onClick={onEdit}>
            Editar
          </Button>
          {pdfUrl && (
            <a
              href={pdfUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="btn btn-sm btn-outline-secondary"
              style={{ borderRadius: '8px' }}
            >
              Ver PDF
            </a>
          )}
          <button
            type="button"
            className="btn btn-sm btn-outline-danger"
            style={{ borderRadius: '8px' }}
            onClick={onDelete}
          >
            Eliminar
          </button>
        </div>
      </div>
    </div>
  )
}

const CourseDetail = () => {
  const { id } = useParams()
  const courseId = Number(id)
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const MySwal = withReactContent(Swal)
  const [selectedVideo, setSelectedVideo] = useState(null)

  const { data: course } = useQuery(
    [TAGS.course_one, courseId],
    () => getCourseOne(courseId),
    {
      enabled: Boolean(courseId),
      select: (data) => data?.res?.course || null,
    }
  )

  const { data: videos = [], isLoading } = useQuery(
    [TAGS.course_videos, courseId],
    () => getVideosByCourse(courseId),
    {
      enabled: Boolean(courseId),
      select: (data) => {
        const list = data?.res?.videos
        return Array.isArray(list)
          ? [...list].sort((a, b) => (a.position ?? 0) - (b.position ?? 0))
          : []
      },
    }
  )

  const { mutate: removeVideo } = useMutation(deleteCourseVideo, {
    onSuccess: ({ response, res }) => {
      if (!response?.ok) {
        Toast({
          icon: 'error',
          title: res?.msg || res?.error || 'No se pudo eliminar el video',
        })
        return
      }
      queryClient.invalidateQueries([TAGS.course_videos, courseId])
      Toast({ title: 'Video eliminado correctamente' })
    },
    onError: () =>
      Toast({ icon: 'error', title: 'No se pudo eliminar el video' }),
  })

  const handleDeleteVideo = (video) => {
    MySwal.fire({
      title: <strong>¿Deseas eliminar este video?</strong>,
      text: 'Esta acción no se puede deshacer.',
      icon: 'warning',
      confirmButtonColor: HUTRIT_PRIMARY_COLOR,
      confirmButtonText: 'Sí, eliminar',
      showCancelButton: true,
      cancelButtonText: 'Cancelar',
    }).then(({ isConfirmed }) => {
      if (isConfirmed) removeVideo({ videoId: video.id })
    })
  }

  const openUpload = () => {
    setSelectedVideo(null)
    handleOpenModal({ id: VIDEO_FORM_MODAL_ID })
  }

  const openEdit = (video) => {
    setSelectedVideo(video)
    handleOpenModal({ id: VIDEO_FORM_MODAL_ID })
  }

  return (
    <DashboardWrapper showTitle title={course?.title || 'Curso'}>
      <VideoFormModal
        courseId={courseId}
        video={selectedVideo}
        onClose={() => setSelectedVideo(null)}
      />

      <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
        <button
          type="button"
          className="btn btn-white border"
          onClick={() => navigate(PRIVATE_ROUTES.master_courses)}
        >
          ← Volver a cursos
        </button>
        <div className="d-flex gap-2 flex-wrap">
          <Button
            variant="secondary"
            onClick={() =>
              navigate(
                PRIVATE_ROUTES.master_course_questions.replace(
                  ':id',
                  String(courseId)
                )
              )
            }
          >
            Preguntas del examen
          </Button>
          <Button onClick={openUpload}>Subir video</Button>
        </div>
      </div>

      {course?.description && (
        <p className="text-on-bg-secondary mb-3">{course.description}</p>
      )}

      <div className="d-flex gap-2 flex-wrap mb-4">
        <span
          className="d-flex align-items-center gap-1 fw-bold"
          style={{
            fontSize: 13,
            padding: '6px 14px',
            borderRadius: 20,
            background: '#eefaf3',
            color: '#01A76A',
            border: '1px solid #cdf0e0',
          }}
        >
          👁 Visitas de talentos: {course?.viewsPublic ?? 0}
        </span>
        <span
          className="d-flex align-items-center gap-1 fw-bold"
          style={{
            fontSize: 13,
            padding: '6px 14px',
            borderRadius: 20,
            background: '#eef4fa',
            color: '#1d6fbf',
            border: '1px solid #cde3f5',
          }}
        >
          🎬 Clases vistas: {course?.viewsTalents ?? 0}
        </span>
      </div>

      {isLoading ? (
        <Loader />
      ) : videos.length === 0 ? (
        <p className="text-on-bg-secondary mb-0">
          Este curso todavía no tiene videos.
        </p>
      ) : (
        <>
          <style>{`
            @media (hover: hover) and (pointer: fine) {
              .video-course-card:hover {
                transform: translateY(-3px);
                box-shadow: 0 14px 32px rgba(1,167,106,0.15);
              }
            }
          `}</style>
          <div className="d-flex flex-column gap-3 w-100">
            {videos.map((video, index) => (
              <CourseVideoCard
                key={video.id}
                video={video}
                displayNumber={index + 1}
                onEdit={() => openEdit(video)}
                onDelete={() => handleDeleteVideo(video)}
              />
            ))}
          </div>
        </>
      )}
    </DashboardWrapper>
  )
}

export default CourseDetail
